import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import Header from "./Header";
import Footer from "./Footer";
import CartButton from "./CartButton";
import { useTranslation } from 'react-i18next';
import { jwtDecode } from "jwt-decode";

const Product = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [priceType, setPriceType] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [sparkle, setSparkle] = useState(false);
  const [activeSale, setActiveSale] = useState(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/";

  const token = localStorage.getItem("token");
  let userId = null;
  if (token) {
    try {
      const decoded = jwtDecode(token);
      userId = decoded.userId;
    } catch (err) {
      console.error("Invalid token:", err);
    }
  }

  useEffect(() => {
    fetch(`${API_URL}product/${id}`)
      .then((res) => res.json())
      .then((data) => setProduct(data))
      .catch((err) => {
        console.error("Error fetching product:", err);
        setError(t("errors.loadProduct"));
      });
  }, [id, API_URL, t]);

  //THIS WAS ADDED BECAUSE MY MOM NEEDED TO REMOVE THE UNIT OPTION, REMOVE THE 5 LINES BELOW IF CAN SELL UNITS AGAIN
    useEffect(() => {
  if (product?.pricing?.box > 0) {
    setPriceType("box");
  }
}, [product]);


useEffect(() => {
    const fetchActiveSale = async () => {
      try {
        const res = await fetch(`${API_URL}active-sitewide-sale`);
        if (!res.ok) return;
        const data = await res.json();
        setActiveSale(data);
      } catch (err) {
        console.error("Failed to fetch active sale:", err);
      }
    };
    fetchActiveSale();
  }, [API_URL]);

  const getSessionCart = () => JSON.parse(sessionStorage.getItem("cart")) || [];
  const addToSessionCart = (productId, priceType) => {
    const cart = getSessionCart();
    const existingItemIndex = cart.findIndex(
      (item) => item.productId === productId && item.priceType === priceType
    );
    if (existingItemIndex > -1) {
      cart[existingItemIndex].quantity += 1;
    } else {
      cart.push({ productId, priceType, quantity: 1 });
    }
    sessionStorage.setItem("cart", JSON.stringify(cart));
  };

  const formatPrice = (price) => {
    const validPrice = isNaN(price) ? 0 : price;
    return validPrice.toFixed(2);
  };

  const isUnavailable = () => {
    const status = product.availability?.fr?.toLowerCase();
    return status === "discontinué" || status === "rupture de stock";
  };

  const getDiscountedPrice = (price) => {
    if (!activeSale || !activeSale.discountPercent) return price;
    return price * (1 - activeSale.discountPercent / 100);
  };
console.log(getDiscountedPrice)
  const addToCart = async () => {
    if (isAdding) return;
    setIsAdding(true);

    if (!userId) {
      addToSessionCart(product._id, priceType);
      setMessage(t("cart.addedGuest"));
      setError("");
      setSparkle(true);
      window.dispatchEvent(new Event("cartUpdated"));
      setTimeout(() => setSparkle(false), 800);
      setTimeout(() => setMessage(""), 3000);
      setTimeout(() => setIsAdding(false), 500);
      return;
    }

    try {
      const res = await fetch(`${API_URL}cart/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId,
          productId: product._id,
          priceType,
          siteWideDiscount: activeSale?.discountPercent || 0,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setMessage(t("cart.added"));
        setError("");
        setSparkle(true);
        window.dispatchEvent(new Event("cartUpdated"));
        setTimeout(() => setSparkle(false), 800);
        setTimeout(() => setMessage(""), 3000);
      } else {
        setError(data.message || t("errors.addToCart"));
        setMessage("");
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      setError(t("errors.generic"));
      setMessage("");
    } finally {
      setTimeout(() => setIsAdding(false), 500);
    }
  };

  if (!product) return <p>{t("loading")}</p>;

  const localized = (field) => product?.[field]?.[lang] || product?.[field]?.fr;

  return (
    <div>
      <Header />
      <ProductWrapper>
        <TopButtons>
          <BackButton onClick={() => navigate("/shop")}>← {t("buttons.backToShop")}</BackButton>
          <SparkleWrapper className={sparkle ? "sparkle" : ""}>
            <CartButton />
            {sparkle && <Sparkle />}
          </SparkleWrapper>
        </TopButtons>

        {product.images?.[0] && (
          <Image src={product.images[0]} alt={localized("name")} />
        )}

        <Info>
          <h2>{localized("name")}</h2>
          <SubCategory>{localized("category")}</SubCategory>
          <p>{localized("description")}</p>

          {/* Kit Contents Display */}
          {product.type === "kit" && product.list?.[lang]?.length > 0 && (
            <KitBox>
              <h3>{t("product.kitIncludes")}:</h3>
              <KitList>
                {product.list[lang].map((item, index) => (
                  <KitItem key={index}>
                    <p>{item}</p>
                  </KitItem>
                ))}
              </KitList>
            </KitBox>
          )}

          {/* Show nutrition only for regular products */}
          {product.type !== "kit" && (
            <>
              <Detail><strong>{t("product.servingPerBox")}:</strong> {localized("servingPerBox")}</Detail>
              <NutrientDiv>
                <DetailNutri><strong>{t("product.calories")}:</strong> {product.calories}</DetailNutri>
                <DetailNutri><strong>{t("product.protein")}:</strong> {product.protein}</DetailNutri>
                <DetailNutri><strong>{t("product.sugar")}:</strong> {product.sugar}</DetailNutri>
                <DetailNutri><strong>{t("product.carb")}:</strong> {product.carb}</DetailNutri>
                <DetailNutri><strong>{t("product.fiber")}:</strong> {product.fiber}</DetailNutri>
              </NutrientDiv>

              <Detail><strong>{t("product.ingredients")}:</strong> {localized("ingredients")}</Detail>
              <Detail><strong>{t("product.allergens")}:</strong> {localized("allergens")?.join(", ") || t("product.none")}</Detail>
              <Detail><strong>{t("product.dietaryRestriction")}:</strong> {localized("dietaryRestriction")?.join(", ") || t("product.none")}</Detail>
              <Detail><strong>{t("product.protocolPhase")}:</strong> {localized("protocolPhase")}</Detail>
              <Detail><strong>{t("product.restricted")}:</strong> {localized("restricted")}</Detail>
            </>
          )}

          <Detail><strong>{t("product.availability")}:</strong> {localized("availability")}</Detail>
{activeSale && (
  <SaleText>{t("sale.enjoySale", { saleName: activeSale.name[lang] })}</SaleText>
)}
          {isUnavailable() && (
            <StatusBubble status={product.availability.fr.toLowerCase()}>
              {localized("availability")}
            </StatusBubble>
          )}

          <PriceSelect>
           
          {product.pricing?.box > 0 && (
            <RadioWrapper selected={priceType === "box"}> 
            
              <HiddenRadio
                type="radio"
                name="price"
                value="box"
                checked={priceType === "box"}
                onChange={() => setPriceType("box")}
              />
              <StyledRadio checked={priceType === "box"}>
                {priceType === "box" && (
                  <Checkmark viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </Checkmark>
                )}
              </StyledRadio>
              <PriceSideBySide>
    {activeSale ? (
      <>
        <OriginalPrice>${formatPrice(product.pricing.box)}</OriginalPrice>
        <DiscountedPrice>
          ${formatPrice(product.pricing.box * (1 - activeSale.discountPercent / 100))}
        </DiscountedPrice>
      </>
    ) : (
      <RegularPrice>${formatPrice(product.pricing.box)}</RegularPrice>
    )}
    <p>{t("product.box")}</p>
  </PriceSideBySide>
            </RadioWrapper>
          )}
{/*           {product.pricing?.unit > 0 && (
  <RadioWrapper selected={priceType === "unit"}>
    <HiddenRadio type="radio" name="price" value="unit" checked={priceType === "unit"} onChange={() => setPriceType("unit")} />
    <StyledRadio checked={priceType === "unit"}>
      {priceType === "unit" && (
        <Checkmark viewBox="0 0 24 24">
          <polyline points="20 6 9 17 4 12" />
        </Checkmark>
      )}
    </StyledRadio>
    <span>
        {activeSale ? (
      <>
        <OriginalPrice>${formatPrice(product.pricing?.unit)}</OriginalPrice>{" "}
      <DiscountedPrice>${formatPrice(product.pricing?.unit * (1 - (activeSale?.discountPercent ?? 0)/100))}</DiscountedPrice>
      </>
    ) : (
      <RegularPrice>${formatPrice(product.pricing.unit)}</RegularPrice>
    )}
    </span>
    <p>{t("product.unit")}</p>
  </RadioWrapper>
)} */}
        </PriceSelect>

          <QcDisclaimer>{t("disclaimer.quebec")}</QcDisclaimer>

          <AddButton onClick={addToCart} disabled={isAdding || isUnavailable() || !priceType} >
            {t("buttons.addToCart")}
          </AddButton>

          {message && <FloatingMessage>{message}</FloatingMessage>}
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <StyledDisclaimer>
            <strong>*Note: </strong>
            <p>{t("disclaimer.note")}</p>
            <br />
            <strong>{t("disclaimer.purchaseNotice")}</strong>
          </StyledDisclaimer>
        </Info>
      </ProductWrapper>
      <Footer />
    </div>
  );
};

export default Product;

const KitBox = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
`;

const SaleText = styled.div`
  color:rgb(43, 129, 43);
  font-weight: bold;
  font-size: 1.3rem;
  margin-bottom: 5px;
  text-transform: uppercase;
  text-shadow: white 2px 2px 2px;
`;

const KitList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;
const PriceSideBySide = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const OriginalPrice = styled.span`
  text-decoration: line-through;
  color: #999;
  font-size: 0.9rem;
`;

const DiscountedPrice = styled.span`
  color:rgb(224, 113, 94);
  font-weight: bold;
  font-size: 1rem;
`;

const RegularPrice = styled.span`
  color: #000;
  font-weight: bold;
  font-size: 1rem;
`;

const KitItem = styled.p`
  margin: 0;
  padding: 0.5rem 0.75rem;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.5;
  white-space: pre-line;
`;

const StatusBubble = styled.div`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  background-color: ${({ status }) =>
    status === "discontinué" ? "#ff5c5c" : "#ffb347"};
  color: white;
  font-weight: bold;
  font-size: 0.9rem;
  margin-bottom: 10px;
  animation: fadeIn 0.5s ease-in-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const QcDisclaimer = styled.p`
  margin-top: 2rem;
  font-size: 0.85rem;
  color: black;
  font-weight: bold;
  background-color: rgba(255,255,255,0.5);
  max-width: 290px;
  padding: 10px 14px;
  border-radius: 10px;
`;

const PriceSelect = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
`;

const RadioWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 2px solid ${props => props.selected ? '#4caf50' : '#000'};
  background-color: ${props => props.selected ? '#e8f5e9' : '#f7f7f7'};
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.selected ? '#000' : 'black'};
  width: fit-content;

  &:hover {
    border-color: #4caf50;
  }
`;

const HiddenRadio = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
`;

const StyledRadio = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  background: ${props => props.checked ? '#4caf50' : '#fff'};
  border-radius: 60px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
  border: 1px solid ${props => props.checked ? '#4caf50' : '#ccc'};
  cursor: pointer;

  svg {
    stroke-dasharray: 22;
    stroke-dashoffset: ${props => props.checked ? '0' : '22'};
    transition: stroke-dashoffset 0.3s ease;
  }
`;

const Checkmark = styled.svg`
  width: 16px;
  height: 16px;
  stroke: white;
  stroke-width: 5;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
`;

const SparkleWrapper = styled.div`
  position: relative;
  display: inline-block;

  &.sparkle {
    animation: pop 0.4s ease;
  }

  @keyframes pop {
    0% { transform: scale(1); }
    50% { transform: scale(1.15); }
    100% { transform: scale(1); }
  }
`;

const Sparkle = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 18px;
  height: 18px;
  background: radial-gradient(circle, #fffacd 0%, #ffeb3b 60%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
  animation: sparkle 0.8s ease forwards;
  z-index: 2;

  @keyframes sparkle {
    0% {
      opacity: 1;
      transform: scale(1) rotate(0deg);
    }
    50% {
      transform: scale(1.6) rotate(45deg);
    }
    100% {
      opacity: 0;
      transform: scale(0.5) rotate(90deg);
    }
  }
`;

const TopButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FloatingMessage = styled.div`
  position: fixed;
  top: 80px;
  right: 20px;
  background-color: #4CAF50;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: fadeOut 3s forwards;
  z-index: 999;

  @keyframes fadeOut {
    0% { opacity: 1; transform: translateY(0); }
    80% { opacity: 1; }
    100% { opacity: 0; transform: translateY(-10px); }
  }
`;

const ProductWrapper = styled.div`
  display: flex;
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
  flex-wrap: wrap;
  gap: 30px;
  padding: 40px;
  background: linear-gradient(122deg, rgba(148,163,135,1) 0%, rgba(186,204,171,1) 43%, rgba(246,187,155,1) 83%, rgba(217,121,97,1) 100%);
  min-height: 80vh;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
  }
`;

const Image = styled.img`
  width: 100%;
  max-width: 400px;
  max-height: 400px;
  border-radius: 16px;
  object-fit: cover;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const Info = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-size: 1rem;
  gap: 12px;

  h2 {
    font-size: 2rem;
    margin-bottom: 1px;
    color: #1e1e1e;
  }

  p {
    margin: 0px 0;
    line-height: 1.4;
  }
`;

const NutrientDiv = styled.div`
display: flex;
gap: 16px
`;

const DetailNutri = styled.p`
  background-color:rgba(248, 247, 247, 0.67);
  margin-right: 100px;
  border: 2px solid black;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 1rem;

`;

const BackButton = styled.button`
  margin-bottom: 20px;
  background-color: #3a5a40;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  align-self: flex-start;

  &:hover {
    background-color: #2d4732;
  }
`;

const AddButton = styled.button`
  background-color: ${props => props.disabled ? '#ccc' : '#3a5a40'};
  color: white;
  border: none;
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 1.1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin-top: 10px;
  align-self: flex-start;

  &:hover {
    background-color: ${props => props.disabled ? '#ccc' : '#2d4732'};
  }
`;

const ErrorMessage = styled.p`
  color: red;
  margin-top: 12px;
  font-weight: bold;
`;

const SubCategory = styled.h3`
  font-size: 1.2rem;
  font-weight: 500;
  margin: 8px 0 16px;
  color: #3a5a40;
`;

const Detail = styled.p`
  margin: 6px 0;
  padding-right: 20px;
  font-size: 0.95rem;

  strong {
    font-weight: 600;
    color: #2d4732;
  }
`;

const StyledDisclaimer = styled.p`
  margin-top: 2rem;
  font-size: 0.85rem;
  color: #444;
  background-color: rgba(255,255,255,0.5);
  padding: 10px 14px;
  border-radius: 10px;
`;